import solr from "../lib/solr";

const log = (...args) => console.debug("[rke:search]", ...args);

const state = {
    highlighting: {},
    hits: 0,
    lastFacetResults: {
        keywords: [],
        motifs: [],
        persons: [],
        places: [],
        verse: [],
    },
    lastSearchQuery: {},
    lastSearchResults: [],
    searchObject: initializeSearchObject(),
    transactions: 0,
};

const SOLR_RESULT_LIMIT = 25;

function buildQueryWithParams(query, filters, offset, limit) {
    if (filters) {
        query = filterQuery(query, filters);
    }

    const params = {
        query: solr.transformQuery(query) || undefined,
        offset: offset > 0 ? offset : 0,
        limit: limit != SOLR_RESULT_LIMIT ? limit : SOLR_RESULT_LIMIT,
    };
    const paramKeys = Object.keys(params);
    const paramsQuery = paramKeys
        .filter((key) => typeof params[key] !== "undefined")
        .map((key) => [key, params[key]])
        .map(([key, value]) => {
            if (Array.isArray(value)) {
                const sub = [];
                for (const v of value) {
                    sub.push([key, encodeURIComponent(v)].join("="));
                }
                return sub.join("&");
            } else {
                return [key, encodeURIComponent(value)].join("=");
            }
        })
        .join("&");
    return paramsQuery;
}

function filterClause(key, value) {
    if (!solr.clientFields.includes(key)) {
        return undefined;
    }

    const fieldIndex = solr.clientFields.indexOf(key);
    const field = solr.solrFields[fieldIndex];
    let values = [];
    if (field === "motif_txts_en") {
        values.push(`"${solrEscape(value)}"`);
    } else {
        values = value.split(/\s+/).map((v) => solrEscape(v));
    }

    // Search both DE/EN titles
    if (key === "titel") {
        const queryParts = values.map(
            (v) => `(title_txt_de:${v} OR title_txt_en:${v})`,
        );
        return queryParts.join(" ");
    }

    return values.map((v) => `${field}:${v}`).join(" ");
}

function filterQuery(query, filters) {
    const filterQuery = Object.keys(filters)
        .map((key) => {
            return Array.from(filters[key]).map((filter) =>
                filterClause(key, filter),
            );
        })
        .flat()
        .filter((c) => c.length > 0)
        .join(" AND ");

    return [query, filterQuery].filter((c) => c).join(" AND ");
}

function initializeSearchObject() {
    return {
        query: "",
        offset: 0,
        limit: 10,
        fieldQueries: solr.clientFields.reduce(
            (aggr, current) => Object.assign(aggr, { [current]: new Set() }),
            {},
        ),
    };
}

function mapRawSolrGroupedResultToStructuredResult(group, results) {
    return group.doclist.docs.map((doc) =>
        mapRawSolrResultToStructuredResult(doc, results),
    );
}

function mapRawSolrResultToStructuredResult(doc, results) {
    return {
        comments: doc.comments_txt_de?.replace(/[\n\r]+/g, " / ") ?? "",
        authority: doc.authority_txt_de?.replace(/[\n\r]+/g, " / ") ?? "",
        proverb: doc.proverb_txt_de?.replace(/[\n\r]+/g, " / ") ?? "",
        highlights: reduceHighlights(results.highlighting[doc.id]),
        id: doc.id.substring(doc.id.lastIndexOf("#") + 1),
        keywords: doc.keyword_ss,
        motifs: doc.motif_txts_en,
        persons: doc.person_ss,
        places: doc.place_ss,
        keywords_pos: doc.keyword_pos_ss,
        motifs_pos: doc.motif_txts_pos_en,
        persons_pos: doc.person_pos_ss,
        places_pos: doc.place_pos_ss,
        solrId: doc.id,
        title: doc.title,
        title_pos: doc.title_pos_txt_de ?? [],
        transcription: doc.transcript_txt_de?.replace(/[\n\r]+/g, " / ") ?? "",
        workId: doc["work_txt_id"],
        isLeading: doc.leading === "true" || doc.leading === true,
        title_en: doc.title_txt_en,
        title_pos_en: doc.title_pos_txt_en ?? [],
        translation: doc.translation_txt_en?.replace(/[\n\r]+/g, " / ") ?? "",
        metadata: doc.metadata_txt_de?.replace(/[\n\r]+/g, " / ") ?? "",
        app: doc.app_txt_de?.replace(/[\n\r]+/g, " / ") ?? "",
        edition: doc.edition_txt_de?.replace(/[\n\r]+/g, " / ") ?? "",
        translation_pos: doc.translation_pos_txt_en ?? [],
        metadata_pos: doc.metadata_pos_txt_de ?? [],
        app_pos: doc.app_pos_txt_de ?? [],
        comments_pos: doc.comments_pos_txt_de ?? [],
        authority_pos: doc.authority_pos_txt_de ?? [],
        proverb_pos: doc.proverb_pos_txt_de ?? [],
        transcription_pos: doc.transcript_pos_txt_de ?? [],
        edition_pos: doc.edition_pos_txt_de ?? [],
        verse: doc.verse_or_prose ?? "Verse",
    };
}

function reduceHighlights(solrHighlights) {
    return Object.keys(solrHighlights)
        .reduce((aggregator, current) => {
            return aggregator.concat(solrHighlights[current]);
        }, [])
        .join(" [...] ")
        .replace(/[\n\r]+/g, " / ");
}

function solrEscape(str) {
    return str.replace(/([!+&|)([\]{}^~:"])/g, "\\$1");
}

const actions = {
    async search({ commit, state }) {
        const { query, fieldQueries, offset, limit } = state.searchObject;
        const url = [
            "/api/search",
            buildQueryWithParams(query, fieldQueries, offset, limit),
        ]
            .filter((c) => c)
            .join("?");
        commit("startTransaction");

        try {
            const response = await fetch(url, { credentials: "same-origin" });
            const results = await response.json();
            commit("addResults", results);
        } catch (err) {
            log(err);
            commit("reset");
        }

        commit("endTransaction");
    },

    async searchKeyword({ commit, dispatch }, keyword) {
        commit("resetPreviousResultsOnly");
        commit("setQuery", `keyword_ss:${keyword}`);
        await dispatch("search");
    },

    async searchPerson({ commit, dispatch }, person) {
        commit("resetPreviousResultsOnly");
        commit("setQuery", `person_ss:${person}`);
        await dispatch("search");
    },

    async loadNext({ commit, state }) {
        const searchTerm = state.lastSearchQuery.q;
        const offset = state.lastSearchResults.length;
        const url = [
            "/api/search",
            buildQueryWithParams(searchTerm, offset, SOLR_RESULT_LIMIT),
        ]
            .filter((c) => c)
            .join("?");
        commit("startTransaction");
        try {
            const response = await fetch(url, { credentials: "same-origin" });
            const results = await response.json();
            commit("addResults", results);
        } catch (err) {
            log(err);
        }

        commit("endTransaction");
    },
};

const getters = {
    lastSearchQueryForDisplay(state) {
        let query = state.lastSearchQuery.q ? state.lastSearchQuery.q : "";
        solr.solrFields.forEach((field) => {
            const fieldIndex = solr.solrFields.indexOf(field);
            query = query.replaceAll(
                `${field}:`,
                `[${solr.clientFields[fieldIndex]}:] `,
            );
        });
        return query;
    },
};

const mutations = {
    addFilter(state, { type, filterQuery }) {
        if (type === "volltext") {
            state.searchObject.query += ` ${filterQuery}`;
        } else {
            state.searchObject.fieldQueries[type].add(filterQuery);
        }
    },

    addResults(state, results) {
        const result = results.results;
        const { keywords, motifs, persons, places, verse } = results;
        state.highlighting = Object.assign(
            state.highlighting,
            result.highlighting,
        );
        ((state.lastFacetResults = {
            keywords,
            motifs,
            persons,
            places,
            verse,
        }),
            (state.lastSearchQuery = result.responseHeader.params));

        // Handle grouped results
        if (result?.grouped?.work_txt_id) {
            // count documents (= groups), not their individual witnesses
            const groups = result.grouped.work_txt_id.groups;
            state.hits = result.grouped.work_txt_id.ngroups ?? 0;
            //state.lastSearchResults = groups.flatMap(group => group.doclist.docs).map((doc) => mapRawSolrResultToStructuredResult(doc, result))
            state.lastSearchResults = groups.map((group) =>
                mapRawSolrGroupedResultToStructuredResult(group, result),
            );
        } else {
            state.hits = result.response.numFound;
            state.lastSearchResults = result.response.docs.map((doc) =>
                mapRawSolrResultToStructuredResult(doc, result),
            );
        }
    },

    endTransaction(state) {
        state.transactions -= 1;
    },

    removeFilter(state, { type, filterQuery }) {
        state.searchObject.fieldQueries[type].delete(filterQuery);
    },

    reset(state) {
        Object.assign(state, {
            highlighting: {},
            hits: 0,
            lastFacetResults: {
                keywords: [],
                motifs: [],
                persons: [],
                places: [],
                verse: [],
            },
            lastSearchQuery: {},
            lastSearchResults: [],
            searchObject: initializeSearchObject(),
        });
    },

    resetPreviousResultsOnly(state) {
        ((state.highlighting = {}),
            (state.hits = 0),
            (state.lastFacetResults = {
                keywords: [],
                motifs: [],
                persons: [],
                places: [],
                verse: [],
            }),
            (state.lastSearchQuery = {}),
            (state.lastSearchResults = []));
    },

    setOffset(state, offset) {
        state.searchObject.offset = offset;
    },

    setQuery(state, query) {
        state.searchObject.query = query;
    },

    setLastSearchQuery(state, searchTerm) {
        state.lastSearchQuery = searchTerm;
    },

    startTransaction(state) {
        state.transactions += 1;
    },
};

export default {
    namespaced: true,
    actions,
    getters,
    mutations,
    state,
};
